// apps/retreat-pwa/src/hooks/useUpdates.ts
import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchUpdates, fetchUpdatesLastReadAt, writeUpdatesLastReadAt, readCacheSync } from '../services/updatesService';
import { useAuthContext } from '../auth/authContext';
import type { PlatformUpdate } from '../types/updates';

type LastReadState =
  | { status: 'loading' }
  | { status: 'ready'; at: Date | null };

interface UpdatesState {
  updates: PlatformUpdate[];
  unreadCount: number;
  markAllRead: () => void;
}

export function useUpdates(): UpdatesState {
  const { session } = useAuthContext();
  const [updates, setUpdates] = useState<PlatformUpdate[]>(readCacheSync);
  const [lastRead, setLastRead] = useState<LastReadState>({ status: 'loading' });

  useEffect(() => {
    fetchUpdates().then(setUpdates);
    fetchUpdatesLastReadAt().then((at) => setLastRead({ status: 'ready', at }));
  }, []);

  const unreadCount = useMemo(() => {
    if (lastRead.status === 'loading') return 0;
    if (lastRead.at === null) return updates.length;
    return updates.filter((u) => u.publishedAt > lastRead.at!).length;
  }, [updates, lastRead]);

  const markAllRead = useCallback(() => {
    const now = new Date();
    setLastRead({ status: 'ready', at: now });
    if (session?.userId) {
      writeUpdatesLastReadAt(session.userId, now);
    }
  }, [session?.userId]);

  return { updates, unreadCount, markAllRead };
}
